import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container/Container';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import { MTableToolbar } from '@material-table/core';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ArrowForwardIosOutlinedIcon from '@material-ui/icons/ArrowForwardIosOutlined';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import MaterialTable from 'components/Table/Table';
import Background from 'components/Layout/Background';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { RootStore } from 'stores/rootStore';
import { AdminPortfoliosStore } from 'stores/admin/adminPortfolios';
import { fractionToPercent, searchInStrings, isoDateTimeToLabelZoned, f2p } from 'helpers/helpers';
import PortfolioModal from './PortfolioModal';
import { Portfolio } from 'stores/admin/adminPortfolios';

interface Stores {
  rootStore: RootStore;
}

const ordinalCodeIsCustom = (ord?: number) => {
  return ord && ord < 100;
};

const Portfolios: React.FC<Stores> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new AdminPortfoliosStore(rootStore));
  const { fetchList, savedPortfolios } = store;

  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);
  const [editId, setEditId] = useState<string | undefined>(undefined);
  const [uid, setUid] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [executionOpen, setExecutionOpen] = useState(false);
  const [clone, setClone] = useState(false);

  useEffect(() => {
    (async () => {
      await fetchList();
    })();
  }, [fetchList, store]);

  const handleRefresh = async () => {
    await fetchList();
  };

  const sortByType = (a: Portfolio, b: Portfolio) => {
    if (a.type === b.type) {
      return sortByCustomType(a, b);
    } else {
      return t(`portfolio_type_${a.type}`).localeCompare(t(`portfolio_type_${b.type}`));
    }
  };

  const sortByCustomType = (a: Portfolio, b: Portfolio) => {
    if (a.customType === b.customType) {
      // can be undefined
      if ((a.intermediaryCode && b.intermediaryCode && a.intermediaryCode === b.intermediaryCode) || a.ordinalCode === b.ordinalCode) {
        // intermediary can be undefined
        return sortByKey(a, b);
      } else if (a.intermediaryCode && b.intermediaryCode) {
        return t(`intermediary_${a.intermediaryCode}_short`).localeCompare(
          t(`intermediary_${b.intermediaryCode}_short`),
        );
      } else if (ordinalCodeIsCustom(a.ordinalCode) && ordinalCodeIsCustom(b.ordinalCode)) {
        return t(`TYPE_CUS_${a.ordinalCode}`).localeCompare(
          t(`TYPE_CUS_${b.ordinalCode}`),
        );
      } else {
        return a.intermediaryCode || ordinalCodeIsCustom(a.ordinalCode) ? 1 : -1;
      }
    } else return t(`portfolio_type_custom_${a.customType}`).localeCompare(t(`portfolio_type_custom_${b.customType}`));
  };

  const sortByKey = (a: Portfolio, b: Portfolio) => {
    const re = new RegExp('(.*[^\\d])(?:(\\d+))?');
    const ap = re.exec(a.key);
    const bp = re.exec(b.key);
    if (ap && bp && ap[2] && bp[2] && ap[1] === bp[1]) {
      return Number(ap[2]) - Number(bp[2]);
    } else {
      return a.key.localeCompare(b.key);
    }
  };

  const sortByRisk = (a: Portfolio, b: Portfolio) => {
    return Number(a.riskProfile) - Number(b.riskProfile);
  };

  const { filters } = store;

  return (
    <>
      <Container maxWidth="xl">
        <Background inside>
          <div style={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="secondary"
              className="secondaryGreen" // new-account-button
              onClick={() => {
                setModalOpen(true);
              }}
              startIcon={<AddIcon fontSize="medium" />}
            >
              {t('portfolio_new')}
            </Button>
          </div>

          {store.list?.length > 0 && (
            <MaterialTable
              //tableRef={tableRef}
              search
              options={{
                sorting: true,
                draggable: false,
                paging: false,
                filtering: true,
              }}
              components={{
                Toolbar: (props) => (
                  <Grid container>
                    <Grid item xs={12}>
                      <MTableToolbar {...props} />
                    </Grid>
                  </Grid>
                ),
              }}
              data={store.list}
              columns={[
                {
                  field: 'title',
                  title: t('general_title'),
                  defaultFilter: filters.title,
                  //cellStyle: { width: '20%' },
                  //headerStyle: { width: '20%' },
                },
                {
                  field: 'type',
                  title: t('general_type'),
                  render: ({ type, customType }) => t(`portfolio_type_${customType === 'CUSTOM' ? 'OTHER' : type}`),
                  customSort: sortByType,
                  customFilterAndSearch: (search, data) => {
                    return (
                      t(`portfolio_type_${data.customType === 'CUSTOM' ? 'OTHER' : data.type}`)
                        .toLowerCase()
                        .indexOf(search.trim().toLowerCase()) > -1
                    );
                  },
                  defaultFilter: filters.type,
                },
                {
                  field: 'customType',
                  title: t('portfolio_customType'),
                  render: ({ customType, intermediaryCode, ordinalCode }) =>
                    !customType ? (
                      ''
                    ) : (
                      <>
                        {t(`portfolio_type_custom_${customType}`)}
                        {intermediaryCode && (
                          <>
                            <br />({t(`intermediary_${intermediaryCode}_short`)})
                          </>
                        )}
                        {ordinalCodeIsCustom(ordinalCode) && (
                          <>
                            <br />({t(`TYPE_CUS_${ordinalCode}`)})
                          </>
                        )}
                      </>
                    ),
                  customSort: sortByCustomType,
                  customFilterAndSearch: (search, data) =>
                    searchInStrings(search, [
                      t(`portfolio_type_custom_${data.customType}`),
                      t(`intermediary_${data.intermediaryCode}_short`),
                    ]),
                  defaultFilter: filters.customType,
                },
                { field: 'key', title: t('general_code'), customSort: sortByKey, defaultFilter: filters.key },
                {
                  field: 'currency',
                  title: t('general_currency'),
                  lookup: {
                    EUR: 'EUR',
                    CZK: 'CZK',
                  },
                  defaultFilter: filters.currency || ['EUR', 'CZK'],
                },
                {
                  field: 'riskProfile',
                  title: t('accountPlan_portfolioTable_risk'),
                  customSort: sortByRisk,
                  defaultFilter: filters.riskProfile,
                },
                {
                  field: 'hedgingEur',
                  title: `${t('general_Hedging')} EUR`,
                  customFilterAndSearch: (search, data) => {
                    return f2p(data.hedgingEur).toString().indexOf(search.trim().toLowerCase()) > -1;
                  },
                  render: ({ hedgingEur }) => fractionToPercent(hedgingEur),
                  defaultFilter: filters.hedgingEur,
                },
                {
                  field: 'hedgingUsd',
                  title: `${t('general_Hedging')} USD`,
                  customFilterAndSearch: (search, data) => {
                    return f2p(data.hedgingUsd).toString().indexOf(search.trim().toLowerCase()) > -1;
                  },
                  render: ({ hedgingUsd }) => fractionToPercent(hedgingUsd),
                  defaultFilter: filters.hedgingUsd,
                },
                {
                  field: 'validFrom',
                  title: t('general_lastUpdate'),
                  customFilterAndSearch: (search, data) => {
                    const searchNormalized = search.trim().replace(/\s/g, '');
                    const dataNormalized = data.validFrom
                      ? isoDateTimeToLabelZoned(data.validFrom, 'dd.MM.yyyyHH:mm')
                      : '';
                    return dataNormalized.indexOf(searchNormalized) > -1;
                  },
                  render: ({ validFrom }) => (validFrom ? isoDateTimeToLabelZoned(validFrom) : ''),
                  defaultFilter: filters.validFrom,
                },
                {
                  field: 'lastUpdate',
                  title: t('portfolio_validFrom'),
                  customFilterAndSearch: (search, data) => {
                    const searchNormalized = search.trim().replace(/\s/g, '');
                    const dataNormalized = data.lastUpdate
                      ? isoDateTimeToLabelZoned(data.lastUpdate, 'dd.MM.yyyyHH:mm')
                      : '';
                    return dataNormalized.indexOf(searchNormalized) > -1;
                  },
                  render: ({ lastUpdate }) => (lastUpdate ? isoDateTimeToLabelZoned(lastUpdate) : ''),
                  defaultFilter: filters.lastUpdate,
                },
                {
                  field: 'designation',
                  filtering: false,
                  sorting: false,
                  render: ({ type, key, currency, ordinalCode, id, readyForUpdate, marking, locked }) => {
                  const markUid = `${type}${key}${currency}${ordinalCode}`;
                  return (
                    <Button
                      startIcon={
                        marking ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : readyForUpdate ? (
                          <CheckOutlinedIcon />
                        ) : (
                          <></>
                        )
                      }
                      variant="contained"
                      color={readyForUpdate ? 'default' : savedPortfolios.ids.includes(markUid) ? 'primary' : 'secondary'}
                      onClick={() => {
                        store.markForUpdate(id, !readyForUpdate);
                      }}
                      disabled={locked}
                      style={{ display: 'inline-flex', width: '215px' }}
                    >
                      {t(`portfolio_readyForUpdate_${readyForUpdate}`)}
                    </Button>
                  )},
                  cellStyle: { width: '14%' },
                  headerStyle: { width: '14%' },
                },
                {
                  field: 'actions',
                  filtering: false,
                  sorting: false,
                  render: (portfolio) => (
                    <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                      <IconButton
                        size="medium"
                        className="accountEdit-button"
                        disabled={portfolio.locked}
                        onClick={() => {
                          setClone(true);
                          setEditId(portfolio.id);
                          setModalOpen(true);
                        }}
                      >
                        <FileCopyOutlinedIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="medium"
                        className="accountEdit-button"
                        onClick={() => {
                          setUid(`${portfolio.type}${portfolio.key}${portfolio.currency}${portfolio.ordinalCode}`);
                          setEditId(portfolio.id);
                          setModalOpen(true);
                        }}
                        disabled={portfolio.locked || portfolio.readyForUpdate}
                      >
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="medium"
                        className="account-delete-button"
                        onClick={() => setConfirmDialog(portfolio.id)}
                        disabled={portfolio.used || portfolio.locked || portfolio.readyForUpdate}
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </div>
                  ),
                },
              ]}
              onFilterChange={store.saveFilters}
            />
          )}

          <div style={{ textAlign: 'right', paddingTop: '16px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                const ok = await store.execute();
                if (ok) {
                  setExecutionOpen(true);
                }
              }}
              disabled={!store.ready}
              endIcon={<ArrowForwardIosOutlinedIcon />}
            >
              {t('portfolio_execute')}
            </Button>
          </div>
        </Background>
      </Container>

      <ConfirmDialog
        noHeader
        isOpen={!!confirmDialog}
        confirm={async () => {
          await store.deletePortfolio(confirmDialog!);
          setConfirmDialog(undefined);
          await fetchList();
        }}
        close={() => {
          setConfirmDialog(undefined);
        }}
      >
        {t('portfolio_confirmDelete')}
      </ConfirmDialog>

      <ConfirmDialog
        noHeader
        isOpen={executionOpen}
        confirm={() => {
          setExecutionOpen(false);
          store.fetchList();
        }}
      >
        {t('portfolio_execution_successful')}
      </ConfirmDialog>

      {modalOpen && (
        <PortfolioModal
          portfolioId={editId}
          uid={uid}
          clone={clone}
          store={store}
          refresh={handleRefresh}
          handleClose={() => {
            setModalOpen(false);
            setClone(false);
            setEditId(undefined);
          }}
        />
      )}
    </>
  );
};

export default compose(inject('rootStore'), observer)(Portfolios);
